import React from 'react';
import { Building } from '../../types/building';
import { Building2, Calendar, CheckCircle, AlertCircle, Archive, RefreshCw, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { getBuildingCompletionPercentage } from '../../lib/localDatabase';

interface BuildingCardProps {
  building: Building;
  onClick: () => void;
  onArchive?: () => void;
  onRestore?: () => void;
  onDelete: () => void;
  isArchived?: boolean;
  canDelete?: boolean;
}

export const BuildingCard: React.FC<BuildingCardProps> = ({ 
  building, 
  onClick, 
  onArchive, 
  onRestore,
  onDelete,
  isArchived = false,
  canDelete = false
}) => {
  // Berechne Statistiken
  const totalApartments = building.floors.reduce(
    (sum, floor) => sum + floor.apartments.length, 
    0
  );
  
  const completedApartments = building.floors.reduce(
    (sum, floor) => sum + floor.apartments.filter(
      apt => ['completed', 'completed_with_cable', 'completed_no_measurement'].includes(apt.status)
    ).length, 
    0
  );
  
  const completionPercentage = getBuildingCompletionPercentage(building);
  
  // Finde das Datum der letzten Aktualisierung
  const lastUpdated = building.floors
    .flatMap(floor => floor.apartments)
    .filter(apt => apt.lastUpdated)
    .sort((a, b) => {
      if (!a.lastUpdated) return 1;
      if (!b.lastUpdated) return -1;
      return new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime();
    })[0]?.lastUpdated;

  const handleArchiveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onArchive) onArchive();
  };

  const handleRestoreClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRestore) onRestore();
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (canDelete) onDelete();
  };

  return (
    <div 
      onClick={onClick}
      className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden relative group"
    >
      {isArchived && (
        <div className="absolute top-0 right-0 bg-amber-500 text-white px-3 py-1 text-xs font-medium rounded-bl-lg">
          Archiviert
        </div>
      )}
      
      <div className="p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-lg shrink-0">
            <Building2 className="h-6 w-6 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {building.name}
            </h3>
            <p className="text-sm text-gray-600 truncate">
              {building.address}
            </p>
          </div>
        </div>
        
        <div className="mt-6 space-y-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-600"></div>
              <span className="text-sm text-gray-700">Fortschritt</span>
            </div>
            <span className="text-sm font-medium text-gray-900">{completionPercentage}%</span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full" 
              style={{ width: `${completionPercentage}%` }}
            ></div>
          </div>
          
          <div className="flex flex-wrap justify-between text-sm text-gray-600 gap-2">
            <div className="flex items-center gap-1">
              <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
              <span className="break-words">{completedApartments} abgeschlossen</span>
            </div>
            <div className="flex items-center gap-1">
              <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" />
              <span className="break-words">{totalApartments - completedApartments} ausstehend</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="border-t border-gray-100 px-6 py-3 bg-gray-50 flex justify-between items-center">
        <div className="flex items-center gap-1 text-xs text-gray-500 max-w-[70%] truncate">
          <Calendar className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">
            {building.archivedAt 
              ? `Archiviert: ${format(new Date(building.archivedAt), 'dd. MMM yyyy', { locale: de })}`
              : lastUpdated 
                ? `Zuletzt aktualisiert: ${format(new Date(lastUpdated), 'dd. MMM yyyy', { locale: de })}`
                : 'Keine Aktualisierungen'
            }
          </span>
        </div>
        
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {onArchive && completionPercentage === 100 && (
            <button
              onClick={handleArchiveClick}
              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
              title="Gebäude archivieren"
            >
              <Archive className="w-4 h-4" />
            </button>
          )}
          
          {onRestore && (
            <button
              onClick={handleRestoreClick}
              className="p-1.5 text-green-600 hover:bg-green-50 rounded-full transition-colors"
              title="Gebäude wiederherstellen"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
          
          {canDelete && (
            <button
              onClick={handleDeleteClick}
              className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
              title="Gebäude löschen"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};